import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { tr } from "@/lib/tr";
import {
  dayKey,
  formatClock,
  getDayPrayers,
  getNextPrayer,
  type PrayerKey,
  type PrayerSlot,
} from "@/lib/prayerTimes";

const STORAGE_KEY = "sida.prayerReminder.v1";

export interface PrayerSettings {
  enabled: boolean;
  lat: number | null;
  lng: number | null;
  cityId: string | null;
  /** Last announced prayer, formatted `<dayKey>:<prayerKey>` — prevents duplicate toasts. */
  lastNotified: string | null;
}

const DEFAULTS: PrayerSettings = { enabled: false, lat: null, lng: null, cityId: null, lastNotified: null };

function read(): PrayerSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    return { ...DEFAULTS, ...(JSON.parse(raw) as Partial<PrayerSettings>) };
  } catch {
    return DEFAULTS;
  }
}

function write(s: PrayerSettings) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch {
    /* storage unavailable — settings stay in memory for this session */
  }
}

export function prayerLabel(key: PrayerKey): string {
  switch (key) {
    case "fajr":
      return tr("الفجر", "Fajr", "Fajr", "فجر");
    case "dhuhr":
      return tr("الظهر", "Dhuhr", "Dhuhr", "ظہر");
    case "asr":
      return tr("العصر", "Asr", "Asr", "عصر");
    case "maghrib":
      return tr("المغرب", "Maghrib", "Maghrib", "مغرب");
    case "isha":
      return tr("العشاء", "Isha", "Isha", "عشاء");
  }
}

export function usePrayerReminder(locale: string) {
  const [settings, setSettings] = useState<PrayerSettings>(() => read());
  const [now, setNow] = useState<Date>(() => new Date());
  const [locating, setLocating] = useState(false);
  const [locationError, setLocationError] = useState(false);
  const notifiedRef = useRef<string | null>(settings.lastNotified);

  const update = useCallback((patch: Partial<PrayerSettings>) => {
    setSettings((prev) => {
      const next = { ...prev, ...patch };
      write(next);
      return next;
    });
  }, []);

  const hasLocation = settings.lat != null && settings.lng != null;
  const active = settings.enabled && hasLocation;

  // Single 60s ticker — only while the reminder is actually running.
  useEffect(() => {
    if (!active) return;
    setNow(new Date());
    const id = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(id);
  }, [active]);

  const dayPrayers = useMemo<PrayerSlot[]>(() => {
    if (!active) return [];
    return getDayPrayers(settings.lat as number, settings.lng as number, now);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, settings.lat, settings.lng, dayKey(now)]);

  const next = useMemo<PrayerSlot | null>(() => {
    if (!active) return null;
    return getNextPrayer(settings.lat as number, settings.lng as number, now);
  }, [active, settings.lat, settings.lng, now]);

  // Quiet in-app toast when a prayer time starts — once per prayer per day.
  useEffect(() => {
    if (!active || dayPrayers.length === 0) return;
    const passed = dayPrayers.filter((p) => p.time.getTime() <= now.getTime());
    const current = passed[passed.length - 1];
    if (!current) return;
    // Only announce within 5 minutes of the entry time, never retroactively.
    if (now.getTime() - current.time.getTime() > 5 * 60_000) return;
    const stamp = `${dayKey(now)}:${current.key}`;
    if (notifiedRef.current === stamp) return;
    notifiedRef.current = stamp;
    update({ lastNotified: stamp });
    toast(tr("حان وقت الصلاة", "Prayer time", "Heure de la prière", "نماز کا وقت"), {
      description: `${prayerLabel(current.key)} · ${formatClock(current.time, locale)}`,
      duration: 6000,
    });
  }, [active, dayPrayers, now, locale, update]);

  const requestLocation = useCallback(() => {
    if (!("geolocation" in navigator)) {
      setLocationError(true);
      return;
    }
    setLocating(true);
    setLocationError(false);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        update({ lat: pos.coords.latitude, lng: pos.coords.longitude, cityId: null });
      },
      () => {
        setLocating(false);
        setLocationError(true);
      },
      { timeout: 10_000, maximumAge: 6 * 60 * 60 * 1000 }
    );
  }, [update]);

  const setEnabled = useCallback(
    (value: boolean) => {
      update({ enabled: value });
      if (value && !hasLocation) requestLocation();
      if (!value) setLocationError(false);
    },
    [hasLocation, requestLocation, update]
  );

  const setCity = useCallback(
    (cityId: string, lat: number, lng: number) => {
      setLocationError(false);
      update({ cityId, lat, lng });
    },
    [update]
  );

  return {
    settings,
    active,
    hasLocation,
    dayPrayers,
    next,
    now,
    locating,
    locationError,
    setEnabled,
    setCity,
    requestLocation,
  };
}
